// Description: Icon button component for the Amira app. Only Submit is supported with oblong version
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './AmiraIconButton2.scss';
import cx from 'classnames';

// Icons
import loadingIcon from '../../../images/icons/spinner.svg';
import SubmitIcon from '../Icons/SendIcon';
import RepeatAudioIcon from '../Icons/RepeatAudioIcon';
import HamburgerMenuIcon from '../Icons/HamburgerMenuIcon';
import BookIcon from '../Icons/BookIcon';
import BookSlashIcon from '../Icons/BookSlashIcon';
import QuestionMarkIcon from '../Icons/QuestionMarkIcon';
import BackspaceIcon from '../Icons/BackspaceIcon';
import ArrowIcon from '../Icons/ArrowIcon';

const ICON_BUTTON_TYPES = {
  SUBMIT: 'SUBMIT',
  REPEAT_AUDIO: 'REPEAT_AUDIO',
  HAMBURGER_MENU: 'HAMBURGER_MENU',
  BOOK: 'BOOK_SLASH',
  QUESTION_MARK: 'QUESTION_MARK',
  BACKSPACE: 'BACKSPACE',
};

const getIcon = (type, oblong) => {
  switch(type) {
    case ICON_BUTTON_TYPES.REPEAT_AUDIO:
      return <RepeatAudioIcon className="icon repeatAudioIcon" />;
    case ICON_BUTTON_TYPES.HAMBURGER_MENU:
      return <HamburgerMenuIcon className="icon hamburgerMenuIcon" />;
    case ICON_BUTTON_TYPES.BOOK:
      return <BookIcon className="icon bookIcon" />;
    case ICON_BUTTON_TYPES.QUESTION_MARK:
      return <QuestionMarkIcon className="icon questionMarkIcon" />;
    case ICON_BUTTON_TYPES.BACKSPACE:
      return <BackspaceIcon className="icon backspaceIcon" />;
    case ICON_BUTTON_TYPES.SUBMIT:
      if (oblong) {
        return <ArrowIcon className="icon submitIcon oblong" alt="Submit Icon" />;
      } 
    default:
      return <SubmitIcon className="icon submitIcon" alt="Submit Icon" />;
  }
}

const getDisabledIcon = (type, oblong) => {
  switch(type) {
    case ICON_BUTTON_TYPES.BOOK:
      return <BookSlashIcon className="icon bookIcon" />;
    default:
      return getIcon(type, oblong);
  }
}

const AmiraIconButton2 = forwardRef(({ 
  type, 
  secondary,
  onClick, 
  disabled, 
  invisible, 
  loading, 
  pointerPosition, 
  showPointer, 
  small, 
  large,
  xl,
  oblong
}, ref) => {
  const [useAnimationColors, setUseAnimationColors] = useState(false);
  const [playAnimation, setPlayAnimation] = useState(false);
  const animationTimeouts = useRef([]);

  const clearAllAnimationTimeouts = () => {
    animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    animationTimeouts.current = [];
  };

  const runAnimation = () => {
    const delay = (ms) => new Promise(resolve => {
      const timeout = setTimeout(resolve, ms);
      animationTimeouts.current.push(timeout);
    });

    const startAnimation = () => {
      // setUseAnimationColors(true);
      return delay(1000);
    };

    const playAnimationStep = () => {
      setPlayAnimation(true);
      return delay(1000);
    };

    const stopAnimationStep = () => {
      setPlayAnimation(false);
      return delay(500);
    };

    const resetAnimationColors = () => {
      // setUseAnimationColors(false);
      return delay(3000);
    };

    startAnimation()
      .then(() => playAnimationStep())
      .then(() => stopAnimationStep())
      .then(() => resetAnimationColors())
      .then(() => runAnimation());
  };

  useEffect(() => {
    if (showPointer) {
      runAnimation();
    } else {
      clearAllAnimationTimeouts();
      setUseAnimationColors(false);
      setPlayAnimation(false);
    }

    return () => {
      clearAllAnimationTimeouts();
      setUseAnimationColors(false);
      setPlayAnimation(false);
    }
  }, [showPointer]);

  return (
    <button
      ref={ref}
      className={cx('amiraIconButton2', {
        disabled,
        invisible,
        pointerTarget: true,
        [pointerPosition]: true,
        showPointer,
        loading,
        small,
        large,
        xl,
        // animate: useAnimationColors,
        animating: playAnimation,
        oblong,
        secondary,
      })}
      onClick={!disabled ? onClick : null}
      disabled={disabled}
    >
      {!oblong && (
        <div className="primaryBorder">
          <div className="secondaryBorder">
            <div className="tertiaryBorder">
              <div className='content'>
                {loading && <img className='loadingIcon' src={loadingIcon} />}
                {!loading && (
                  <>
                    {!disabled && getIcon(type, oblong)}
                    {disabled && getDisabledIcon(type, oblong)}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {oblong && (
        <>
          {loading && <img className='loadingIcon' src={loadingIcon} />}
          {!loading && (
            <>
              {!disabled && getIcon(type, oblong)}
              {disabled && getDisabledIcon(type, oblong)}
            </>
          )}
        </>
      )}  
    </button>
  );
});

AmiraIconButton2.propTypes = {
  secondary: PropTypes.bool,
  type: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  invisible: PropTypes.bool,
  loading: PropTypes.bool,
  pointerPosition: PropTypes.string,
  showPointer: PropTypes.bool,
  small: PropTypes.bool,
  large: PropTypes.bool,
};

AmiraIconButton2.defaultProps = {
  secondary: false,
  disabled: false,
  invisible: false,
  loading: false,
  pointerPosition: '',
  showPointer: false,
  small: false,
  large: false,
};

export default AmiraIconButton2;
export {
  ICON_BUTTON_TYPES,
}
